import { createFileRoute, Outlet } from "@tanstack/react-router"

import { graphql } from "#gql"
import type { AircraftSource } from "#gql/graphql.ts"
import { queryKeys } from "#lib/query-keys.ts"
import { AircraftByIdDocument } from "#pages/authenticated/fleet/[aircraftId]/aircraft-layout.tsx"
import { FleetListDocument } from "#pages/authenticated/fleet/page.tsx"
import { client } from "#utils/graphql-client.ts"

const AircraftNameDocument = graphql(/* GraphQL */ `
  query AircraftName($input: QueryAircraftByIdInput!) {
    aircraftById(input: $input) {
      id
      tail
      manufacturer
      model
    }
  }
`)

export const Route = createFileRoute("/_authenticated/$slug/fleet/$aircraftId")({
  loader: async ({ context: { queryClient, userInfo }, params: { aircraftId } }) => {
    if (userInfo.state !== "LoggedIn") return { aircraft: null }

    const orgId = userInfo.me.organization.id
    const fleetData = await queryClient.ensureQueryData({
      queryKey: queryKeys.aircraft.list(orgId),
      queryFn: () =>
        client.request(FleetListDocument, {
          input: { organizationId: orgId, sources: ["LEON", "MANUAL"] },
        }),
    })
    const source: AircraftSource =
      fleetData.aircraftByOrganization.find((a) => a.id === aircraftId)?.source ??
      "MANUAL"

    const [nameData] = await Promise.all([
      queryClient.ensureQueryData({
        queryKey: queryKeys.aircraft.name(aircraftId),
        queryFn: () =>
          client.request(AircraftNameDocument, {
            input: {
              source,
              id: aircraftId,
              organizationId: orgId,
            },
          }),
      }),
      queryClient.ensureQueryData({
        queryKey: queryKeys.aircraft.detail(aircraftId),
        queryFn: () =>
          client.request(AircraftByIdDocument, {
            input: {
              source,
              id: aircraftId,
              organizationId: orgId,
            },
          }),
      }),
    ])
    return {
      aircraft: nameData.aircraftById,
      title: nameData.aircraftById.tail,
    }
  },
  staticData: { title: "Aircraft" },
  component: () => <Outlet />,
})
